import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, EMPTY, from, map, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {
  changePassword,
  changePasswordFailure,
  changePasswordSuccess,
  loadSession,
  login,
  loginError,
  loginSuccess,
  logout,
  logoutError,
  logoutSuccess,
  resendOtp,
  resendOtpFailure,
  resendOtpSuccess,
  setSignUpEmail,
  signUp,
  signUpError,
  signUpSuccess,
  userInfo,
  verifyOtp,
  verifyOtpFailure,
  verifyOtpSuccess
} from './authentication.actions';
import {AuthenticationApiService} from '../services/authentication-api.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationService} from '../services/authentication.service';
import {Store} from '@ngrx/store';
import {GlobalState} from '../../../store/store.model';
import {loadProfile} from '../../profile/store/profile.actions';
import {selectUrl} from '../../../store/router.selectors';
import {SecureStorage} from '@aparajita/capacitor-secure-storage';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {ErrorDialogComponent} from '../../../shared/components/error-dialog/error-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SuccessDialogComponent} from '../../../shared/components/success-dialog/success-dialog.component';
import {selectGuest} from '../../flow/store/flow.selectors';

@Injectable()
export class AuthenticationEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store<GlobalState>);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _authenticationApiService = inject(AuthenticationApiService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(MatDialog);
  private transloco = inject(TranslocoService);

  private _login = createEffect(() => {
    return this._actions$.pipe(
      ofType(login),
      switchMap(({request, lang}) => {
        return this._authenticationApiService.login(request, lang).pipe(
          map((response) => loginSuccess({response})),
          catchError((error) => of(loginError({error})))
        )
      })
    );
  });

  private _signUp = createEffect(() => {
    return this._actions$.pipe(
      ofType(signUp),
      switchMap(({request}) => {
        return this._authenticationApiService.signUp(request).pipe(
          map((response) => signUpSuccess({response})),
          catchError((error) => of(signUpError({error})))
        )
      })
    );
  });

  private _userInfo = createEffect(() => {
    return this._actions$.pipe(
      ofType(userInfo),
      switchMap(() =>
        this._authenticationService._getLastAccessedUser().pipe( // Recupera userId dallo storage
          switchMap(userId => {
            if (!userId) {
              return of(loginError({error: 'User ID not found'}));
            }
            return this._authenticationApiService.userInfo('userId', userId).pipe(
              map(response => loginSuccess({response})),
              catchError(error => {
                console.error('DEBUG: Errore nella chiamata userInfo():', error);
                return of(loginError({error}));
              })
            );
          })
        )
      )
    );
  });


  private _loginSuccess = createEffect(() => {
    return this._actions$.pipe(
      ofType(loginSuccess),
      withLatestFrom(this._store.select(selectUrl)),
      tap(([{response, error}, url]) => {
        if (!error) {
          if (response.redirect) {
            this._authenticationService.allowValidationAccess();
            this._store.dispatch(setSignUpEmail({email: response.redirect?.email}));
            this._router.navigate(['sign-up/validation']);
          } else {
            this._store.dispatch(loadProfile());
            this._authenticationService.finalizeLogin(response, true);
            const path = window.location.pathname;
            if (!path?.includes('tabs')) {
              this._router.navigate(['/tabs']);
            }
          }
        } else {
          this._snackBar.open(error)
        }
        catchError((error) => of(logoutError({error})))
      })
    );
  }, {dispatch: false});

  private _signupSuccess = createEffect(() => {
    return this._actions$.pipe(
      ofType(signUpSuccess),
      tap(({response, error}) => {
        if (!error) {
          const email = typeof response?.result.data.email === 'string' ? response?.result.data.email : '';
          this._authenticationService.allowValidationAccess();
          this._store.dispatch(setSignUpEmail({email: email}));
          this._router.navigate(['sign-up/validation']);
        } else {
          this._snackBar.open(error, 'Ok', {duration: 3000, panelClass: 'snackbar'});
        }
      }),
      catchError((error) => of(signUpError({error})))
    );
  }, {dispatch: false});

  private _signUpError = createEffect(() => {
    return this._actions$.pipe(
      ofType(signUpError),
      tap(({error}) => {
        this._dialog.open(ErrorDialogComponent, {
          data: {
            message: error,
          }
        });
      }),
    );
  }, {dispatch: false});


  private _loadSession = createEffect(() => {
    return this._actions$.pipe(
      ofType(loadSession),
      withLatestFrom(this._store.select(selectGuest)),
      switchMap(([{}, guestMode]) =>
        this._authenticationService._getLastAccessedUser().pipe(
          switchMap((user) => {
            if (!user || guestMode) {
              return EMPTY;
            } else {
              return from(SecureStorage.get(`${user}.sessionId`)).pipe(
                map(result => result),
                map(sessionId => {
                  if (user && sessionId) {
                    return loginSuccess({response: {userId: user, sessionId: String(sessionId)}});
                  } else {
                    return logoutSuccess();
                  }
                })
              );
            }
          }),
          catchError(() => of(logoutSuccess()))
        )
      )
    );
  });

  private _verifyOtp = createEffect(() => {
    return this._actions$.pipe(
      ofType(verifyOtp),
      switchMap(({otp, email}) => {
        return this._authenticationApiService.verifyOtp(otp, email).pipe(
          map(() => verifyOtpSuccess()),
          catchError(error => of(verifyOtpFailure({error})))
        )
      })
    );
  });

  resendOtp$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(resendOtp),
      switchMap(({email}) => {
        return this._authenticationApiService.resendOtp(email).pipe(
          map(() => resendOtpSuccess()),
          catchError(error => of(resendOtpFailure({error})))
        )
      })
    );
  });


  private _logout = createEffect(() => {
    return this._actions$.pipe(
      ofType(logout),
      switchMap(() =>
        this._authenticationApiService.logout().pipe(
          map(() => logoutSuccess()),
          catchError((error) => of(logoutError({error})))
        )
      )
    );
  });

  private _logoutSuccess = createEffect(() =>
      this._actions$.pipe(
        ofType(logoutSuccess),
        tap(() => this._authenticationService.clearAllStorage()),
        switchMap(() =>
          from(this._router.navigate(['/log-in'])).pipe(
            catchError((error) => {
              console.error('Navigation error after logout:', error);
              return EMPTY;
            })
          )
        )
      ),
    {dispatch: false}
  );


  private _verifyOtpSuccess$ = createEffect(() =>
      this._actions$.pipe(
        ofType(verifyOtpSuccess),
        tap(() => {
          const success = this._dialog.open(SuccessDialogComponent, {
            data: {
              message: this.transloco.translate('auth.confirm.success_message')
            }
          });
          success.afterClosed().pipe(tap(() => this._router.navigate(['/log-in']))).subscribe();
        })
      ),
    {dispatch: false}
  );

  private _verifyOtpFailure$ = createEffect(() =>
      this._actions$.pipe(
        ofType(verifyOtpFailure),
        tap(() => {
          this._dialog.open(ErrorDialogComponent, {
            data: {
              message: this.transloco.translate('auth.confirm.error_invalid_otp'),
            }
          });
        })
      ),
    {dispatch: false}
  );

  resendOtpSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(resendOtpSuccess),
        tap(() => {
          this._snackBar.open(
            this.transloco.translate('auth.confirm.resend_success'),
            this.transloco.translate('common.close'),
            {duration: 3000, panelClass: 'snackbar'}
          );
        })
      ),
    {dispatch: false}
  );

  resendOtpFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(resendOtpFailure),
        tap(({error}) => {
          this._dialog.open(ErrorDialogComponent, {
            data: {
              message: error,
            }
          });
        }),
      ),
    {dispatch: false}
  );

  private _changePassword = createEffect(() => {
      return this._actions$.pipe(
        ofType(changePassword),
        switchMap(({otp, email, password, confirmPassword}) => {
            return this._authenticationApiService.changePassword(otp, email, password, confirmPassword).pipe(
              map(() => changePasswordSuccess()),
              catchError(error => of(changePasswordFailure({error})))
            )
          }
        )
      )
    }
  );

  private _changePasswordSuccess = createEffect(() =>
      this._actions$.pipe(
        ofType(changePasswordSuccess),
        tap(() => {
          const success = this._dialog.open(SuccessDialogComponent, {
            data: {
              message: this.transloco.translate('auth.reset.password_changed')
            }
          });
          success.afterClosed().pipe(tap(() => this._router.navigate(['/log-in']))).subscribe();
        })
      ),
    {dispatch: false}
  );

  private _changePasswordFailure = createEffect(
    () =>
      this._actions$.pipe(
        ofType(changePasswordFailure),
        tap(({error}) => {
          this._dialog.open(ErrorDialogComponent, {
            data: {
              message: error,
            }
          });
        }),
      ),
    {dispatch: false}
  );
}
